import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toSession, type TokenBundle } from "../auth/api";
import { useAuth } from "../auth/context";

// Email magic links point here.  We POST the token from JS so that mail
// scanners (which only do HEAD/GET) can't pre-consume it.

type ConsumeState =
  | { kind: "working" }
  | { kind: "error"; reason: string };

export function AuthVerify() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const ran = useRef(false);
  const [state, setState] = useState<ConsumeState>({ kind: "working" });

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      navigate("/auth/error?reason=missing_token", { replace: true });
      return;
    }
    // Strip the token from the URL bar immediately so it doesn't sit in
    // history if consumption fails and the user lands here later.
    history.replaceState(null, "", "/auth/verify");

    void (async () => {
      let res: Response;
      try {
        res = await fetch("/api/auth/magic-link/consume", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
      } catch (err) {
        setState({
          kind: "error",
          reason: err instanceof Error ? err.message : "network_error",
        });
        return;
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        navigate(
          `/auth/error?reason=${encodeURIComponent(body.error ?? "consume_failed")}`,
          { replace: true },
        );
        return;
      }
      const bundle = (await res.json()) as TokenBundle;
      await setSession(
        toSession({
          access_token: bundle.access_token,
          refresh_token: bundle.refresh_token,
        }),
      );
      navigate("/", { replace: true });
    })();
  }, [token, navigate, setSession]);

  return (
    <main className="mx-auto flex min-h-full max-w-md items-center justify-center p-8">
      {state.kind === "working" ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">Signing you in…</p>
      ) : (
        <div className="space-y-2 text-center">
          <p className="text-sm text-rose-700 dark:text-rose-300">Couldn't reach the server.</p>
          <p className="font-mono text-xs text-slate-500 dark:text-slate-400">{state.reason}</p>
        </div>
      )}
    </main>
  );
}
